// ============================================================================
// KLINISOL CRO - Password Reset Modal (REDESIGNED)
// Pure Tailwind CSS - Matches Dashboard/Navbar design system
// Admin-Assisted Password Reset - FDA 21 CFR Part 11 Compliant
// COLOR SCHEME: Slate Grey (#475569) & Magenta (#E91E63)
// ============================================================================

import React, { useState } from 'react';
import api from '../services/api';
import { 
    X, 
    Lock, 
    Unlock, 
    RotateCcw, 
    CheckCircle, 
    Copy, 
    AlertTriangle, 
    Shield,
    ChevronDown,
    ChevronRight,
    Loader2,
    KeyRound
} from 'lucide-react';

const PasswordResetModal = ({ user, onClose }) => {
    const [reason, setReason] = useState('');
    const [loading, setLoading] = useState(false);
    const [newPassword, setNewPassword] = useState(null);
    const [showUnlockOptions, setShowUnlockOptions] = useState(false);

    // ========================================================================
    // HANDLERS
    // ========================================================================
    
    const handleResetPassword = async () => {
        if (!window.confirm(`Reset password for ${user.first_name} ${user.last_name}?`)) {
            return;
        }

        setLoading(true);

        try {
            const response = await api.post(`/password-reset/admin-reset/${user.id}`, {
                reason: reason.trim() || undefined
            });

            if (response.data.success) {
                setNewPassword(response.data.data.temporaryPassword);
            }
        } catch (error) {
            console.error('Password reset error:', error);
            alert(error.response?.data?.message || 'Failed to reset password');
        } finally {
            setLoading(false);
        }
    };

    const handleUnlockAccount = async () => {
        if (!window.confirm(`Unlock account for ${user.first_name} ${user.last_name}?`)) {
            return;
        }

        setLoading(true);

        try {
            const response = await api.post(`/password-reset/unlock/${user.id}`);

            if (response.data.success) {
                alert('Account unlocked successfully');
                onClose();
            }
        } catch (error) {
            console.error('Unlock account error:', error);
            alert(error.response?.data?.message || 'Failed to unlock account');
        } finally {
            setLoading(false);
        }
    };

    const handleResetAttempts = async () => {
        setLoading(true);

        try {
            const response = await api.post(`/password-reset/reset-attempts/${user.id}`);

            if (response.data.success) {
                alert('Failed login attempts reset successfully');
                onClose();
            }
        } catch (error) {
            console.error('Reset attempts error:', error);
            alert(error.response?.data?.message || 'Failed to reset login attempts');
        } finally {
            setLoading(false);
        }
    };

    const handleCopyPassword = () => {
        navigator.clipboard.writeText(newPassword);
        alert('Password copied to clipboard!');
    };

    const handlePasswordModalClose = () => {
        setNewPassword(null);
        onClose();
    };

    // ========================================================================
    // RENDER - NEW PASSWORD DISPLAY
    // ========================================================================
    
    if (newPassword) {
        return (
            <div className="fixed inset-0 bg-slate-900/75 backdrop-blur-sm flex items-center justify-center z-[1000] p-4 animate-fadeIn" onClick={handlePasswordModalClose}>
                <div className="bg-white rounded-3xl max-w-2xl w-full shadow-2xl animate-slideUp" onClick={(e) => e.stopPropagation()}>
                    {/* Success Header */}
                    <div className="bg-gradient-to-r from-green-500 to-green-600 p-7 rounded-t-3xl flex items-center gap-4 text-white">
                        <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                            <CheckCircle className="w-7 h-7 text-white" />
                        </div>
                        <h2 className="text-2xl font-black">Password Reset Successfully</h2>
                    </div>
                    
                    <div className="p-8">
                        <div className="bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-300 rounded-xl p-6 mb-6">
                            <p className="text-base text-green-900">
                                Password reset for <strong>{user.first_name} {user.last_name}</strong>
                            </p>
                        </div>

                        <div className="mb-6">
                            <label className="flex items-center gap-2 text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">
                                <KeyRound className="w-5 h-5 text-[#475569]" />
                                New Temporary Password
                            </label>
                            <div className="bg-gray-900 rounded-xl p-4 flex items-center justify-between gap-4 border-2 border-gray-700">
                                <code className="text-xl font-mono font-bold text-green-400 tracking-wider flex-1">
                                    {newPassword}
                                </code>
                                <button 
                                    onClick={handleCopyPassword}
                                    className="px-4 py-2 bg-[#475569] text-white rounded-lg hover:bg-[#334155] transition-all flex items-center gap-2 font-semibold"
                                    title="Copy to clipboard"
                                >
                                    <Copy className="w-4 h-4" />
                                    Copy
                                </button>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-orange-50 to-orange-100 border-2 border-orange-300 rounded-xl p-6">
                            <div className="flex items-start gap-3 mb-4">
                                <AlertTriangle className="w-6 h-6 text-orange-600 flex-shrink-0 mt-0.5" />
                                <h4 className="text-base font-bold text-orange-900">Important Instructions:</h4>
                            </div>
                            <ol className="space-y-2 text-sm text-orange-900 pl-9 list-decimal list-outside">
                                <li>Copy this password and give it to the user securely</li>
                                <li>User <strong>MUST</strong> change this password on next login</li>
                                <li>This password will not be shown again</li>
                                <li>User email: <strong>{user.email}</strong></li>
                                {reason && <li>Reset reason: <strong>{reason}</strong></li>}
                            </ol>
                        </div>
                    </div>

                    <div className="p-6 border-t-2 border-gray-200 flex justify-end">
                        <button 
                            onClick={handlePasswordModalClose}
                            className="px-8 py-3.5 bg-gradient-to-r from-[#475569] to-[#334155] text-white rounded-xl font-bold hover:-translate-y-0.5 transition-all shadow-lg"
                        >
                            I've Copied the Password - Close
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ========================================================================
    // RENDER - PASSWORD RESET OPTIONS
    // ========================================================================
    
    return (
        <div className="fixed inset-0 bg-slate-900/75 backdrop-blur-sm flex items-center justify-center z-[1000] p-4 animate-fadeIn" onClick={onClose}>
            <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-auto shadow-2xl animate-slideUp" onClick={(e) => e.stopPropagation()}>
                {/* Modal Header */}
                <div className="bg-gradient-to-r from-[#475569] to-[#334155] p-7 rounded-t-3xl flex justify-between items-center text-white">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                            <Lock className="w-6 h-6 text-white" />
                        </div>
                        <h2 className="text-2xl font-black">
                            Password & Account Management
                        </h2>
                    </div>
                    <button 
                        onClick={onClose}
                        className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg text-white hover:bg-white/30 transition-all flex items-center justify-center"
                        disabled={loading}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-8">
                    {/* User Info Box */}
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-[#475569] rounded-xl p-6 mb-8">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                            {user.first_name} {user.last_name}
                        </h3>
                        <p className="text-base text-gray-700 mb-2">{user.email}</p>
                        <div className="flex items-center gap-2 mb-3">
                            <Shield className="w-4 h-4 text-[#475569]" />
                            <p className="text-sm text-gray-700">
                                Role: <strong>{user.role_name}</strong>
                            </p>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            {user.must_change_password && (
                                <span className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-100 border border-yellow-400 text-yellow-800 text-xs font-semibold rounded-full">
                                    <AlertTriangle className="w-3 h-3" />
                                    Must Change Password
                                </span>
                            )}
                            {user.failed_login_attempts > 0 && (
                                <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 border border-red-400 text-red-800 text-xs font-semibold rounded-full">
                                    <AlertTriangle className="w-3 h-3" />
                                    Failed Attempts: {user.failed_login_attempts}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Password Reset Section */}
                    <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6">
                        <div className="flex items-center gap-2 mb-3">
                            <KeyRound className="w-5 h-5 text-[#475569]" />
                            <h4 className="text-lg font-bold text-gray-900">Reset Password</h4>
                        </div>
                        <p className="text-sm text-gray-600 mb-4">
                            Generate a new temporary password for this user. They will be required to change it on next login.
                        </p>
                        
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-900 mb-2">
                                Reason for Reset <span className="text-gray-500 font-normal">(Optional)</span>
                            </label>
                            <textarea
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="e.g., User forgot password, Security policy update, etc."
                                rows="3"
                                disabled={loading}
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm transition-all bg-gray-50 focus:outline-none focus:border-[#475569] focus:bg-white focus:ring-4 focus:ring-[#475569]/10 resize-vertical disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                        </div>

                        <button
                            onClick={handleResetPassword}
                            className={`w-full px-6 py-3.5 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${
                                loading
                                    ? 'bg-gray-400 text-white cursor-not-allowed'
                                    : 'bg-gradient-to-r from-[#475569] to-[#334155] text-white hover:-translate-y-0.5 shadow-lg'
                            }`}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    <span>Resetting Password...</span>
                                </>
                            ) : (
                                <>
                                    <KeyRound className="w-5 h-5" />
                                    <span>Reset Password</span>
                                </>
                            )}
                        </button>
                    </div>

                    {/* Additional Options */}
                    <div className="border-t-2 border-gray-200 pt-6">
                        <button 
                            onClick={() => setShowUnlockOptions(!showUnlockOptions)}
                            className="flex items-center gap-2 text-[#475569] hover:text-[#334155] font-semibold transition-colors"
                        >
                            {showUnlockOptions ? (
                                <ChevronDown className="w-5 h-5" />
                            ) : (
                                <ChevronRight className="w-5 h-5" />
                            )}
                            <span>Additional Account Options</span>
                        </button>

                        {showUnlockOptions && (
                            <div className="mt-6 space-y-4">
                                {/* Unlock Account */}
                                <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                                    <div className="flex items-center gap-2 mb-3">
                                        <Unlock className="w-5 h-5 text-orange-600" />
                                        <h4 className="text-lg font-bold text-gray-900">Unlock Account</h4>
                                    </div>
                                    <p className="text-sm text-gray-600 mb-4">
                                        Remove account lock due to failed login attempts.
                                    </p>
                                    <button
                                        onClick={handleUnlockAccount}
                                        className="w-full px-6 py-3.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-bold hover:-translate-y-0.5 transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        disabled={loading}
                                    >
                                        <Unlock className="w-5 h-5" />
                                        <span>Unlock Account</span>
                                    </button>
                                </div>

                                {/* Reset Attempts */}
                                <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                                    <div className="flex items-center gap-2 mb-3">
                                        <RotateCcw className="w-5 h-5 text-purple-600" />
                                        <h4 className="text-lg font-bold text-gray-900">Reset Failed Login Attempts</h4>
                                    </div>
                                    <p className="text-sm text-gray-600 mb-4">
                                        Reset the failed login attempts counter to zero.
                                    </p>
                                    <button
                                        onClick={handleResetAttempts}
                                        className="w-full px-6 py-3.5 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl font-bold hover:-translate-y-0.5 transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        disabled={loading}
                                    >
                                        <RotateCcw className="w-5 h-5" />
                                        <span>Reset Attempts Counter</span>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="p-6 border-t-2 border-gray-200 flex justify-end">
                    <button 
                        onClick={onClose}
                        className="px-8 py-3.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={loading}
                    >
                        Cancel
                    </button>
                </div>
            </div>

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(50px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
        </div>
    );
};

export default PasswordResetModal;